import { Injectable } from '@angular/core';
import {
  OrganizationApi,
  BusinessPremiseApi,
  ElectronicDeviceApi,
  LoopBackFilter,
  ElectronicDevice,
  BusinessPremise,
} from '../../shared/sdk';
import { FileUploader } from 'ng2-file-upload';
import { environment } from '../../../environments/environment';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class FursService {
  constructor(
    private orgApi: OrganizationApi,
    private businessPremiseApi: BusinessPremiseApi,
    private edApi: ElectronicDeviceApi,
  ) {}

  createBusinessPremise(
    orgId: string,
    premiseId: string,
    premise: BusinessPremise,
  ) {
    return this.orgApi.createBusinessPremises(orgId, premise);
  }

  createDevice(
    orgId: string,
    premiseId: string,
    device: ElectronicDevice,
  ): Observable<ElectronicDevice> {
    const body = device;
    body.organizationId = orgId;

    return this.businessPremiseApi
      .createElectronicDevices(premiseId, body)
      .pipe(map(({ body }) => body));
  }

  deleteBusinessPremise(premiseId: string): Observable<{ count: number }> {
    return this.businessPremiseApi.deleteById(premiseId);
  }

  getDevices(
    orgId: string,
    filter = {},
  ): Observable<{ body: ElectronicDevice[]; totalCount: number }> {
    return this.orgApi.getElectronicDevices(orgId, filter);
  }

  getPremises(
    orgId: string,
    filter?: LoopBackFilter,
  ): Observable<{ body: BusinessPremise[]; totalCount: number }> {
    return this.orgApi.getBusinessPremises(orgId, filter);
  }

  hasCertificate(
    orgId: string,
    type: string,
    env = !environment.production ? 'test' : 'production',
  ): Observable<boolean> {
    return this.orgApi
      .hasCertificate(orgId, type, env)
      .pipe(map(({ body }) => !!body.exists));
  }

  updateBusinessPremise(
    orgId: string,
    premiseId: string,
    premise: Partial<BusinessPremise>,
  ): Observable<BusinessPremise> {
    return this.businessPremiseApi
      .patchAttributes(premiseId, premise)
      .pipe(map(({ body }) => body));
  }

  updateDevice(deviceId: string, device: Partial<ElectronicDevice>) {
    return this.edApi
      .patchAttributes(deviceId, device)
      .pipe(map(({ body }) => body));
  }

  uploadCertificate(
    id: string,
    certificate,
    passphrase: string,
    token: string,
    provider = 'furs',
    test = !environment.production,
  ) {
    let env = '';
    if (test) {
      env = '&environment=test';
    }

    return new Promise((resolve, reject) => {
      const uploader = new FileUploader({
        url: `${environment.apiUrl}/v1/organizations/${id}/upload-certificate?type=${provider}${env}`,
        authToken: `${token}`,
        headers: [
          { name: 'passphrase', value: passphrase },
          { name: 'Access-Control-Allow-Origin', value: '*' },
          { name: 'Access-Control-Allow-Credentials', value: 'false' },
        ],
      });

      uploader.onAfterAddingFile = (file) => {
        file.withCredentials = false;
      };

      const file = new File([certificate], 'certificate');
      uploader.addToQueue([file]);
      uploader.uploadAll();

      const responseSub = uploader.response.subscribe((event) => {
        const fileMeta = JSON.parse(event);
        responseSub.unsubscribe();

        if (fileMeta.success) {
          return resolve(fileMeta);
        }

        reject(fileMeta);
      });
    });
  }
}
